import { useState } from 'react';
import {
  Modal,
  Stack,
  Switch,
  Button,
  Alert,
  Text,
  Group,
  Badge,
  Loader,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconBell,
  IconBellOff,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useNotifications } from '../useNotifications';
import logger from '@ayocore/platform/common/helpers/logger';

interface PushSettingsProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * PushSettings - Component for managing push notification settings
 *
 * Provides UI for enabling/disabling push notifications and sending test notifications.
 */
export function PushSettings({ opened, onClose }: PushSettingsProps) {
  const {
    pushSupported,
    pushEnabled,
    pushPermission,
    pushLoading,
    enablePush,
    disablePush,
    sendTestPush,
    checkPushPermission,
  } = useNotifications();

  const [testLoading, setTestLoading] = useState(false);

  // Handle push toggle
  const handlePushToggle = async (enabled: boolean) => {
    if (enabled) {
      await enablePush();
    } else {
      await disablePush();
    }
    // Refresh permission status
    checkPushPermission();
  };

  // Handle test push
  const handleTestPush = async () => {
    setTestLoading(true);
    try {
      await sendTestPush();
      // Логи об успешной отправке уже пишет NotificationController
    } catch (error) {
      logger.error('❌ Error sending test push:', error);
    } finally {
      setTestLoading(false);
    }
  };

  // Get status badge
  const getStatusBadge = () => {
    if (!pushSupported) {
      return (
        <Badge color='red' leftSection={<IconX size={12} />}>
          Не поддерживается
        </Badge>
      );
    }

    switch (pushPermission) {
      case 'granted':
        return (
          <Badge color='green' leftSection={<IconCheck size={12} />}>
            Включено
          </Badge>
        );
      case 'denied':
        return (
          <Badge color='red' leftSection={<IconX size={12} />}>
            Запрещено
          </Badge>
        );
      case 'default':
        return (
          <Badge color='yellow' leftSection={<IconInfoCircle size={12} />}>
            Не настроено
          </Badge>
        );
      default:
        return <Badge color='gray'>Неизвестно</Badge>;
    }
  };

  // Get permission message
  const getPermissionMessage = () => {
    if (!pushSupported) {
      return 'Ваш браузер не поддерживает push-уведомления.';
    }

    switch (pushPermission) {
      case 'granted':
        return 'Push-уведомления разрешены и работают.';
      case 'denied':
        return 'Push-уведомления запрещены. Разрешите их в настройках браузера.';
      case 'default':
        return 'Push-уведомления не настроены. Включите их для получения уведомлений.';
      default:
        return 'Статус push-уведомлений неизвестен.';
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap='sm'>
          <IconBell size={20} />
          <Text fw={600}>Настройки push-уведомлений</Text>
        </Group>
      }
      size='md'
      centered
    >
      <Stack gap='md'>
        {/* Status Overview */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          color={pushSupported ? 'blue' : 'red'}
          variant='light'
        >
          <Text size='sm'>{getPermissionMessage()}</Text>
        </Alert>

        {/* Current Status */}
        <Group justify='space-between'>
          <Text fw={500}>Текущий статус:</Text>
          {getStatusBadge()}
        </Group>

        {/* Push Toggle */}
        {pushSupported && (
          <Group justify='space-between'>
            <div>
              <Text fw={500}>Push-уведомления</Text>
              <Text size='sm' color='dimmed'>
                Получать уведомления даже когда вкладка закрыта
              </Text>
            </div>
            <Switch
              checked={pushEnabled}
              onChange={event => handlePushToggle(event.currentTarget.checked)}
              disabled={pushLoading || pushPermission === 'denied'}
              size='md'
              color='blue'
            />
          </Group>
        )}

        {/* Loading State */}
        {pushLoading && (
          <Group gap='sm'>
            <Loader size='sm' />
            <Text size='sm' color='dimmed'>
              Настройка push-уведомлений...
            </Text>
          </Group>
        )}

        {/* Browser Not Supported */}
        {!pushSupported && (
          <Alert icon={<IconAlertCircle size={16} />} color='red' variant='light'>
            <Text size='sm'>
              Ваш браузер не поддерживает push-уведомления. Попробуйте использовать Chrome, Firefox,
              Safari или Edge.
            </Text>
          </Alert>
        )}

        {/* Permission Denied */}
        {pushSupported && pushPermission === 'denied' && (
          <Alert icon={<IconAlertCircle size={16} />} color='orange' variant='light'>
            <Text size='sm'>
              Push-уведомления запрещены в настройках браузера. Чтобы включить их:
            </Text>
            <Text size='sm' mt='xs'>
              1. Нажмите на иконку замка рядом с адресом сайта
              <br />
              2. Выберите "Разрешить" для уведомлений
              <br />
              3. Обновите страницу
            </Text>
          </Alert>
        )}

        {/* Test Button */}
        {pushSupported && pushEnabled && (
          <Group justify='space-between'>
            <div>
              <Text fw={500}>Тестовое уведомление</Text>
              <Text size='sm' color='dimmed'>
                Отправить тестовое push-уведомление
              </Text>
            </div>
            <Button
              onClick={handleTestPush}
              loading={testLoading}
              disabled={pushLoading}
              size='sm'
              variant='outline'
              leftSection={<IconBell size={16} />}
            >
              Отправить тест
            </Button>
          </Group>
        )}

        {/* Instructions */}
        {pushSupported && pushPermission === 'default' && (
          <Alert icon={<IconInfoCircle size={16} />} color='blue' variant='light'>
            <Text size='sm' fw={500}>
              Как работают push-уведомления:
            </Text>
            <Text size='sm' mt='xs'>
              • Вы будете получать уведомления даже когда сайт закрыт
              <br />
              • Уведомления появляются в системном трее
              <br />
              • Вы можете отключить их в любой момент
              <br />• Данные передаются в зашифрованном виде
            </Text>
          </Alert>
        )}

        {/* Close Button */}
        <Group justify='flex-end' mt='md'>
          <Button onClick={onClose} variant='outline'>
            Закрыть
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * PushSettingsButton - Compact button to open push settings
 */
export function PushSettingsButton() {
  const { pushSupported, pushEnabled, pushPermission } = useNotifications();
  const [opened, setOpened] = useState(false);

  // Get icon based on status
  const getIcon = () => {
    if (!pushSupported) return IconBellOff;
    if (pushEnabled) return IconBell;
    if (pushPermission === 'denied') return IconBellOff;
    return IconBellOff;
  };

  // Get color based on status
  const getColor = () => {
    if (!pushSupported) return 'gray';
    if (pushEnabled) return 'green';
    if (pushPermission === 'denied') return 'red';
    return 'yellow';
  };

  const Icon = getIcon();
  const color = getColor();

  return (
    <>
      <Tooltip label='Настройки push-уведомлений'>
        <ActionIcon onClick={() => setOpened(true)} color={color} variant='light' size='input-xs'>
          <Icon size={16} />
        </ActionIcon>
      </Tooltip>

      <PushSettings opened={opened} onClose={() => setOpened(false)} />
    </>
  );
}
